import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ThemeOption } from 'src/app/models/_core/theme-option';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly storageKey = 'themePreference';
  private readonly legacyKey = 'prefersDark';
  private readonly mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  private readonly mediaListener = (event: MediaQueryListEvent) => {
    if (this.preference === 'system') {
      this.applyResolved(event.matches);
    }
  };

  private preference: ThemeOption = this.readStoredPreference();
  private readonly darkSubject = new BehaviorSubject<boolean>(
    this.resolveIsDark(this.preference),
  );
  readonly isDark$ = this.darkSubject.asObservable();

  constructor(private authService: AuthService) {
    this.applyDom(this.darkSubject.value);
    this.syncMediaListener();
  }

  /** Current dark-mode state (resolved from preference + system). */
  get isDark(): boolean {
    return this.darkSubject.value;
  }

  /** Current theme preference for select controls (light | dark | system). */
  get theme(): ThemeOption {
    return this.preference;
  }

  /**
   * Apply stored / env theme preference on app start.
   * Call once from AppComponent.
   */
  initFromEnvironment(): void {
    switch (environment.theme) {
      case 'light':
        this.setTheme('light');
        break;
      case 'dark':
        this.setTheme('dark');
        break;
      case 'user':
      default:
        this.setTheme(this.readStoredPreference());
        break;
    }
  }

  setTheme(theme: ThemeOption): void {
    this.preference = theme;
    localStorage.setItem(this.storageKey, theme);
    // Keep legacy key in sync for any older readers
    if (theme === 'system') {
      localStorage.removeItem(this.legacyKey);
    } else {
      localStorage.setItem(this.legacyKey, String(theme === 'dark'));
    }
    this.authService.setTheme(theme);
    this.syncMediaListener();
    this.applyResolved(this.resolveIsDark(theme));
  }

  setDark(isDark: boolean): void {
    this.setTheme(isDark ? 'dark' : 'light');
  }

  private applyResolved(isDark: boolean): void {
    this.applyDom(isDark);
    this.darkSubject.next(isDark);
  }

  private resolveIsDark(theme: ThemeOption): boolean {
    if (theme === 'system') {
      return this.mediaQuery.matches;
    }
    return theme === 'dark';
  }

  private syncMediaListener(): void {
    this.mediaQuery.removeEventListener('change', this.mediaListener);
    if (this.preference === 'system') {
      this.mediaQuery.addEventListener('change', this.mediaListener);
    }
  }

  private applyDom(isDark: boolean): void {
    document.documentElement.classList.toggle('ion-palette-dark', isDark);
    this.syncColorSchemeMeta(isDark);
  }

  private readStoredPreference(): ThemeOption {
    const stored = localStorage.getItem(this.storageKey);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored;
    }

    // Migrate from legacy boolean preference
    const prefersDark = localStorage.getItem(this.legacyKey);
    if (prefersDark === 'true') {
      return 'dark';
    }
    if (prefersDark === 'false') {
      return 'light';
    }

    return 'system';
  }

  private syncColorSchemeMeta(isDark: boolean): void {
    const head = document.getElementsByTagName('head')[0];
    const existing = document.querySelectorAll('meta[name="color-scheme"]');
    existing.forEach((tag) => tag.parentNode?.removeChild(tag));

    if (isDark) {
      const metaTag = document.createElement('meta');
      metaTag.name = 'color-scheme';
      metaTag.content = 'light dark';
      head.appendChild(metaTag);
    }
  }
}
